import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { DrillPanels } from "../components/drill/panels";
import { DrillProvider } from "../components/drill/provider";
import { CommandBar } from "../components/layout/header";
import { Loading } from "../components/kit";
import { Sidebar } from "../components/ui/sidebar";
import { useT } from "../i18n/provider";
import { installEscapeListener } from "../lib/drill/stack";
import { useCapabilities, useSession } from "../lib/session";

/**
 * The shell — docs/24 §5. A LAYOUT ROUTE, not a `layout.tsx`: this is a Vite SPA
 * on react-router, so there is no App Router and nothing server-rendered.
 */
export function Shell() {
  const { i18n } = useT();
  const { state } = useSession();
  const { passkeyBacked } = useCapabilities();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // ONE Escape listener for every window on the glass (§7.6), installed here so
  // it lives exactly as long as the console does.
  useEffect(installEscapeListener, []);

  if (state.status === "loading") {
    // §11.8: the wait happens inside a page shape, not as grey text against the
    // window's edge with the whole layout appearing around it when data lands.
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="w-64">
          <Loading rows={3} label={i18n._("Checking your session…")} />
        </div>
      </div>
    );
  }

  if (state.status !== "ready") {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/sign-in?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  /**
   * An ADMIN or FINANCE session that was not opened with a passkey legitimately
   * exists — the api marks the enrolment endpoints `@AllowEnrolment()` so a new
   * admin can register their first passkey, and such a session reaches `/v1/me`
   * and nothing else. Sending it to the enrolment screen is the honest response;
   * drawing the console would be a shell whose every register answers 403.
   */
  if (!passkeyBacked && location.pathname !== "/enrol") {
    return <Navigate to="/enrol" replace />;
  }

  return (
    <DrillProvider>
      <div className="flex h-full flex-col overflow-hidden">
        <CommandBar />
        <div className="flex min-h-0 flex-1">
          <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((on) => !on)} />
          {/* §5: the content frame has ONE ROUNDED CORNER — 8px on the top-left
              only, with a hairline on the two edges that meet the ground.
              Settings, the Store and Photos are all this shape; it is what says
              «Windows 11» before anything else does. `md:` only — a rounded
              corner against nothing is a notch. */}
          <main className="content-frame flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-t border-l border-border bg-layer md:rounded-tl-xl">
            <Outlet />
          </main>
        </div>
      </div>
      <DrillPanels />
    </DrillProvider>
  );
}
